import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Trophy, Swords, Clock } from 'lucide-react';
import type { TennisChallengeEncounter } from '@/hooks/useTennisChallengeEncounters';
import { useLanguage } from '@/contexts/LanguageContext';

interface TennisChallengeCardProps {
  encounter: TennisChallengeEncounter;
  weekNumber: number;
  dateLabel?: string;
}

const TennisChallengeCard: React.FC<TennisChallengeCardProps> = ({ encounter, weekNumber, dateLabel }) => {
  const { t } = useLanguage();

  const getResultBadge = () => {
    switch (encounter.result) {
      case 'won':
        return (
          <Badge className="bg-green-100 text-green-700 border-green-300">
            <Trophy className="h-3 w-3 mr-1" />
            {t('league.won')}
          </Badge>
        );
      case 'lost':
        return (
          <Badge className="bg-red-100 text-red-700 border-red-300">
            {t('league.lost')}
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-amber-100 text-amber-700 border-amber-300">
            <Clock className="h-3 w-3 mr-1" />
            {t('league.pendingResult')}
          </Badge>
        );
    }
  };

  return (
    <div className="bg-card rounded-lg border p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Swords className="h-4 w-4 text-primary" />
          <div>
            <span className="text-sm font-semibold">{t('league.yourChallengeWeek').replace('{n}', String(weekNumber))}</span>
            {dateLabel && (
              <p className="text-xs text-muted-foreground">{dateLabel}</p>
            )}
          </div>
        </div>
        {getResultBadge()}
      </div>

      <div className="space-y-2">
        <div className={`flex items-center justify-between p-2 rounded-md ${encounter.isUserChallenger ? 'bg-primary/5 border border-primary/20' : 'bg-muted/50'}`}>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">{t('league.challenger')}</span>
            <span className="font-semibold text-sm">
              {encounter.isUserChallenger ? t('league.youLabel') : encounter.challengerName}
            </span>
          </div>
          <Badge variant="outline" className="text-xs">
            #{encounter.challengerPosition}
          </Badge>
        </div>

        <div className="flex items-center justify-center gap-2">
          <div className="h-px w-8 bg-border"></div>
          <span className="text-xs font-bold text-muted-foreground">VS</span>
          <div className="h-px w-8 bg-border"></div>
        </div>

        <div className={`flex items-center justify-between p-2 rounded-md ${encounter.isUserChallenged ? 'bg-primary/5 border border-primary/20' : 'bg-muted/50'}`}>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">{t('league.challenged')}</span>
            <span className="font-semibold text-sm">
              {encounter.isUserChallenged ? t('league.youLabel') : encounter.challengedName}
            </span>
          </div>
          <Badge variant="outline" className="text-xs">
            #{encounter.challengedPosition}
          </Badge>
        </div>
      </div>
    </div>
  );
};

export default TennisChallengeCard;
